import {
  Navigate,
  Route,
  RouterProvider,
  Routes,
  createBrowserRouter,
} from "react-router-dom";
import { AssetFigurePage } from "./assets/AssetFigurePage";
import { AssetMaterialPage } from "./assets/AssetMaterialPage";
import { AssetShapePage } from "./assets/AssetShapePage";
import { AssetSkinPage } from "./assets/AssetSkinPage";
import { AssetsPage } from "./assets/AssetsPage";
import { HelpPage } from "./assets/HelpPage";
import { ImportPage } from "./assets/ImportPage";
import { ImportReviewPage } from "./assets/ImportReviewPage";
import { ImportsPage } from "./assets/ImportsPage";
import { InspectorRoute } from "./assets/inspector";
import { Composer } from "./ad/Composer";
import { HomePage } from "./home/HomePage";
import { DesignListPage } from "./modeling/DesignListPage";
import { DesignPage } from "./modeling/DesignPage";
import { NewDesignPage } from "./modeling/NewDesignPage";
import { AdPage } from "./pages/AdPage";
import { AdsPage } from "./pages/AdsPage";
import { AgentPage } from "./pages/AgentPage";
import { BrandPage } from "./pages/BrandPage";
import { MediaPage } from "./pages/MediaPage";
import { LoginPage } from "./pages/LoginPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { ReviewPage } from "./pages/ReviewPage";
import { RequireFeature } from "./shell/RequireFeature";
import { Shell } from "./shell/Shell";

/** The route table (docs/SPEC.md §25). Exported without a router so tests can
 *  mount it in a memory router. The home page, `/login` and the public review
 *  page `/r/:token` sit outside the shell: no session is needed to see them,
 *  no redirect to sign in, no balance chip. The app proper starts at `/ads`. */
export function AppRoutes() {
  return (
    <Routes>
      <Route index element={<HomePage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/r/:token" element={<ReviewPage />} />
      <Route element={<Shell />}>
        <Route path="/new" element={<Composer />} />
        <Route path="/ads" element={<AdsPage />} />
        <Route path="/ads/:id" element={<AdPage />} />
        {/* The old path's pages went with P25-T01: an old address lands on
            the list of ads. */}
        <Route path="/jobs/*" element={<Navigate to="/ads" replace />} />
        <Route path="/media" element={<MediaPage />} />
        {/* the Media page was `/library` until 2026-10-14 */}
        <Route path="/library" element={<Navigate to="/media" replace />} />
        <Route
          path="/assets"
          element={
            <RequireFeature name="assetlib">
              <AssetsPage />
            </RequireFeature>
          }
        />
        {/* The four inspector routes are written out one by one: with no
            `:kind` parameter, `/assets/import` can never be mistaken for one
            and `/assets/anything/x` is not found. `InspectorRoute` is the
            feature's gate and asks for the 3D view's code at once. */}
        <Route
          path="/assets/shape/:id"
          element={
            <InspectorRoute>
              <AssetShapePage />
            </InspectorRoute>
          }
        />
        <Route
          path="/assets/variant/:id"
          element={
            <InspectorRoute>
              <AssetShapePage />
            </InspectorRoute>
          }
        />
        <Route
          path="/assets/material/:id"
          element={
            <InspectorRoute>
              <AssetMaterialPage />
            </InspectorRoute>
          }
        />
        <Route
          path="/assets/skin/:id"
          element={
            <InspectorRoute>
              <AssetSkinPage />
            </InspectorRoute>
          }
        />
        {/* A figure has no 3D view (the viewer has no skinning, P29): the
            page is its picture and its words. */}
        <Route
          path="/assets/figure/:id"
          element={
            <RequireFeature name="assetlib">
              <AssetFigurePage />
            </RequireFeature>
          }
        />
        {/* Bringing a model of one's own in: the form, the list of what
            waits, and the page of one model (its check and its review). */}
        <Route
          path="/assets/import"
          element={
            <RequireFeature name="imports">
              <ImportPage />
            </RequireFeature>
          }
        />
        <Route
          path="/assets/imports"
          element={
            <RequireFeature name="imports">
              <ImportsPage />
            </RequireFeature>
          }
        />
        <Route
          path="/assets/imports/:id"
          element={
            <RequireFeature name="imports">
              <ImportReviewPage />
            </RequireFeature>
          }
        />
        {/* The design studio. `/assets/studio/new` is declared before
            `/assets/studio/:id`. */}
        <Route
          path="/assets/studio"
          element={
            <RequireFeature name="studio">
              <DesignListPage />
            </RequireFeature>
          }
        />
        <Route
          path="/assets/studio/new"
          element={
            <RequireFeature name="studio">
              <NewDesignPage />
            </RequireFeature>
          }
        />
        <Route
          path="/assets/studio/:id"
          element={
            <RequireFeature name="studio">
              <DesignPage />
            </RequireFeature>
          }
        />
        <Route
          path="/help/3d-library"
          element={
            <RequireFeature name="assetlib">
              <HelpPage />
            </RequireFeature>
          }
        />
        <Route path="/brand" element={<BrandPage />} />
        <Route path="/agent" element={<AgentPage />} />
        <Route path="/agent/pair" element={<AgentPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}

// A data router, because the object page asks before it is left with unsaved
// changes (`useBlocker`). The route table itself stays the `<Routes>` above.
const router = createBrowserRouter([{ path: "*", element: <AppRoutes /> }]);

export default function App() {
  return <RouterProvider router={router} />;
}
